import React, { useCallback, useMemo } from 'react';
import { CollectionManagementHeader } from 'components/common/general/CollectionManagementPanel';
import { observer } from 'mobx-react-lite';
import { useIMQAddEmployeesPageStore } from 'store/pages/quote/imq-add-employees/pageStore';
import { EmployeesStatusFilterDropdown } from 'components/common/add-employees/EmployeesStatusFilterDropdown';
import { debounce } from 'global/utils/debounce';
import { SubProgressFilterDropdown } from 'components/common/rfq/add-employess/SubProgressFilterDropdown';
import { useRootStore } from 'store/rootStore';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { IMQAddEmployeesDropdown } from '../IMQAddEmployeesDropdown';
import { HeaderSectionStyled, InviteAllButtonStyled } from './styles';
import { TABLE_MANAGEMENT_PANEL_TEST_IDS } from '../constants';

export const IMQTableManagementPanel: React.FC = observer(() => {
  const { paginationDelegate, employees, inviteAllEmployees, inviteAllModalDelegate, isLoading } =
    useIMQAddEmployeesPageStore();
  const { domainStoreV2 } = useRootStore();

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        paginationDelegate.setFilterValue('employeeName', searchValue);
      }, 300),
    [paginationDelegate],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  const handleInviteAllConfirm = useCallback(async () => {
    inviteAllModalDelegate.close();
    await inviteAllEmployees();
  }, [inviteAllEmployees, inviteAllModalDelegate]);

  return (
    <>
      <CollectionManagementHeader
        elementsTitle="employees"
        page={paginationDelegate.page}
        currentElements={employees.length}
        totalElements={paginationDelegate.totalElements}
        elementsPerPage={12}
        onSearch={handleSearch}
        searchInputPlaceholder="Employee Name"
        headerSection={
          <HeaderSectionStyled>
            <IMQAddEmployeesDropdown availableAddEmployeesActions={domainStoreV2.availableAddEmployeesActions} />
            <EmployeesStatusFilterDropdown paginationDelegate={paginationDelegate} />
            <SubProgressFilterDropdown paginationDelegate={paginationDelegate} />
            {domainStoreV2.showSelfEnrollFeature && (
              <InviteAllButtonStyled
                color="primary"
                size="large"
                onClick={inviteAllModalDelegate.open}
                disabled={isLoading}
                data-test-id={TABLE_MANAGEMENT_PANEL_TEST_IDS.INVITE_ALL_BUTTON}
              >
                Invite All
              </InviteAllButtonStyled>
            )}
          </HeaderSectionStyled>
        }
      />
      <ActionModal
        title="Invite All"
        subtitle="Are you sure you want to send invitations to all employees?"
        show={inviteAllModalDelegate.isVisible}
        onClose={inviteAllModalDelegate.close}
        closeButtonText="Cancel"
        actionButtonText="Invite All"
        onAction={handleInviteAllConfirm}
        modalType={ModalType.EMAIL}
        data-test-id={TABLE_MANAGEMENT_PANEL_TEST_IDS.INVITE_ALL_MODAL}
      />
    </>
  );
});
